import { useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ApiError } from '../api/client';
import { Alert } from '../components/common';
import { useAuth } from '../state/AuthContext';

/** Where to land after signing in — the page the customer was trying to reach. */
function useReturnPath() {
  const location = useLocation();

  return (location.state as { from?: string } | null)?.from ?? '/';
}

function useSubmitHandler() {
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({});
  const [busy, setBusy] = useState(false);

  async function run(action: () => Promise<void>) {
    setError(null);
    setFieldErrors({});
    setBusy(true);

    try {
      await action();
    } catch (e) {
      if (e instanceof ApiError) {
        setError(e.message);
        setFieldErrors(e.fieldErrors ?? {});
      } else {
        setError((e as Error).message);
      }

      setBusy(false);
    }
  }

  return { error, fieldErrors, busy, run };
}

function FieldError({ errors }: { errors?: string[] }) {
  if (!errors?.length) return null;

  return <span className="field__error">{errors.join(' ')}</span>;
}

export function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const returnPath = useReturnPath();
  const { error, fieldErrors, busy, run } = useSubmitHandler();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  function submit(event: FormEvent) {
    event.preventDefault();

    void run(async () => {
      await login(email, password);
      navigate(returnPath, { replace: true });
    });
  }

  return (
    <section className="page page--narrow">
      <h1>Sign in</h1>
      <p className="muted">Welcome back. Sign in to place your order.</p>

      {error && <Alert>{error}</Alert>}

      <form className="form" onSubmit={submit}>
        <label className="field">
          <span>Email</span>
          <input
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <FieldError errors={fieldErrors.email} />
        </label>

        <label className="field">
          <span>Password</span>
          <input
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <FieldError errors={fieldErrors.password} />
        </label>

        <button type="submit" className="btn btn--primary btn--block" disabled={busy}>
          {busy ? 'Signing in…' : 'Sign in'}
        </button>
      </form>

      <p className="center muted">
        <Link to="/forgot-password">Forgotten your password?</Link>
      </p>

      <p className="center muted">
        New here? <Link to="/register">Create an account</Link>
      </p>
    </section>
  );
}

export function RegisterPage() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const returnPath = useReturnPath();
  const { error, fieldErrors, busy, run } = useSubmitHandler();

  const [form, setForm] = useState({
    fullName: '',
    email: '',
    phone: '',
    password: '',
    marketingOptIn: true,
    notifyBySms: false,
  });

  function update<K extends keyof typeof form>(field: K, value: (typeof form)[K]) {
    setForm((current) => ({ ...current, [field]: value }));
  }

  function submit(event: FormEvent) {
    event.preventDefault();

    void run(async () => {
      await register(form);
      navigate(returnPath, { replace: true });
    });
  }

  return (
    <section className="page page--narrow">
      <h1>Create your account</h1>
      <p className="muted">
        One account, faster checkout, and your order history in one place.
      </p>

      {error && <Alert>{error}</Alert>}

      <form className="form" onSubmit={submit}>
        <label className="field">
          <span>Full name</span>
          <input
            autoComplete="name"
            required
            value={form.fullName}
            onChange={(e) => update('fullName', e.target.value)}
          />
          <FieldError errors={fieldErrors.fullName} />
        </label>

        <label className="field">
          <span>Email</span>
          <input
            type="email"
            autoComplete="email"
            required
            value={form.email}
            onChange={(e) => update('email', e.target.value)}
          />
          <FieldError errors={fieldErrors.email} />
        </label>

        <label className="field">
          <span>Mobile number</span>
          <input
            inputMode="tel"
            autoComplete="tel"
            required
            placeholder="0729820176"
            value={form.phone}
            onChange={(e) => update('phone', e.target.value)}
          />
          <FieldError errors={fieldErrors.phone} />
        </label>

        <label className="field">
          <span>Password</span>
          <input
            type="password"
            autoComplete="new-password"
            required
            minLength={8}
            value={form.password}
            onChange={(e) => update('password', e.target.value)}
          />
          <span className="muted small">At least 8 characters, with a letter and a number.</span>
          <FieldError errors={fieldErrors.password} />
        </label>

        <label className="check">
          <input
            type="checkbox"
            checked={form.notifyBySms}
            onChange={(e) => update('notifyBySms', e.target.checked)}
          />
          <span>Text me when my order is on its way</span>
        </label>

        <label className="check">
          <input
            type="checkbox"
            checked={form.marketingOptIn}
            onChange={(e) => update('marketingOptIn', e.target.checked)}
          />
          <span>Email me specials and new menu items</span>
        </label>

        <p className="muted small">
          You always get an email confirming your order and when it is on the way.
        </p>

        <button type="submit" className="btn btn--primary btn--block" disabled={busy}>
          {busy ? 'Creating your account…' : 'Create account'}
        </button>
      </form>

      <p className="center muted">
        Already have an account? <Link to="/login">Sign in</Link>
      </p>
    </section>
  );
}
